import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
  UseQueryOptions,
} from '@tanstack/react-query';
import { useSnackbar } from 'notistack';

import { apiClient } from '@api';
import { routes } from '@app/routes';
import { useFeatureFlags } from '@features/feature-flags';
import { AssessmentStatus, FeatureFlagKey } from '@libs/constants';
import { useTrans } from '@libs/i18n';
import {
  CreateAssessmentRequest,
  CreateAssessmentResponse,
  CreateVersionParam,
  CreateVersionResponse,
  DeleteAssessmentParam,
  DeleteAssessmentResponse,
  DuplicateAssessmentParam,
  DuplicateAssessmentResponse,
  GetAllAssessmentsQueries,
  GetAllAssessmentsResponse,
  GetAssessmentByIdParam,
  GetAssessmentByIdQuery,
  GetAssessmentByIdResponse,
  GetAssessmentResultsResponse,
  GetAssessmentResultsResponseSchema,
  GetAssessmentStatusParam,
  GetAssessmentStatusResponse,
  GetFrameworkDetailsParams,
  GetFrameworkDetailsResponse,
  GetReportDownloadUrlParam,
  GetReportDownloadUrlResponse,
  SubmitAssessmentParam,
  SubmitAssessmentResponse,
  UpdateAssessmentTitleResponse,
} from '@libs/types';

import { queryKeys } from '../../../constants/query-keys';
import { getLocalizedErrorMessage } from '../utils/error-utils';

// ============================================================================
// SERVICE FUNCTIONS - Assessment CRUD Operations
// ============================================================================

/**
 * Get all assessments with optional filters
 * Input: searchBy, status, timeFilter, remediationState, page, limit, sortBy, order
 * Output: Paginated list of assessments
 */
async function getAllAssessments(
  params?: Partial<GetAllAssessmentsQueries>,
  signal?: AbortSignal,
): Promise<GetAllAssessmentsResponse> {
  const queryParams = new URLSearchParams();

  if (params?.searchBy) queryParams.append('searchBy', params.searchBy);
  if (params?.status) queryParams.append('status', params.status);
  if (params?.timeFilter) queryParams.append('timeFilter', params.timeFilter);
  if (params?.remediationState) queryParams.append('remediationState', params.remediationState);
  // Only sent when opting in — the endpoint's default is false.
  if (params?.includeRemediationState) queryParams.append('includeRemediationState', 'true');
  if (params?.page) queryParams.append('page', params.page.toString());
  if (params?.limit) queryParams.append('limit', params.limit.toString());
  if (params?.sortBy) queryParams.append('sortBy', params.sortBy);
  if (params?.order) queryParams.append('order', params.order);

  const response = await apiClient.get<GetAllAssessmentsResponse>(
    'api/assessments',
    {
      params: queryParams,
      signal,
    },
  );

  return response.data;
}

/**
 * Delete an assessment
 * Input: assessmentId (string)
 * Output: Confirmation message
 */
async function deleteAssessment(params: DeleteAssessmentParam): Promise<DeleteAssessmentResponse> {
  const response = await apiClient.delete<DeleteAssessmentResponse>(
    `api/assessments/${params.assessmentId}`,
  );

  return response.data;
}

/**
 * Create a new assessment
 * Input: Assessment data (title, frameworkId, language)
 * Output: Created assessment with generated ID
 */
async function createAssessment(data: CreateAssessmentRequest): Promise<CreateAssessmentResponse> {
  const response = await apiClient.post<CreateAssessmentResponse>(
    'api/assessments',
    data,
  );

  return response.data;
}

/**
 * Update assessment title
 * Input: assessmentId (string), title (string)
 * Output: Updated assessment title with assessmentId and new title
 */
async function updateAssessmentTitle(
  assessmentId: string,
  title: string,
): Promise<UpdateAssessmentTitleResponse> {
  const response = await apiClient.patch<UpdateAssessmentTitleResponse>(
    `api/assessments/${assessmentId}/title`,
    { title },
  );

  return response.data;
}

/**
 * Get assessment by ID with optional query filters
 * Input: assessmentId (string), includeFramework (boolean), includeDomains (boolean)
 * Output: Assessment details with optional relations
 */
async function getAssessmentById(
  params: GetAssessmentByIdParam,
  query?: GetAssessmentByIdQuery,
  signal?: AbortSignal,
): Promise<GetAssessmentByIdResponse> {
  const queryParams = new URLSearchParams();

  if (query?.includeDomains) {
    queryParams.append('includeDomains', query.includeDomains.toString());
  }

  if (query?.includePreviousAssessmentSummary) {
    queryParams.append(
      'includePreviousAssessmentSummary',
      query.includePreviousAssessmentSummary.toString(),
    );
  }

  if (query?.includePreviousDomainsSummary) {
    queryParams.append(
      'includePreviousDomainsSummary',
      query.includePreviousDomainsSummary.toString(),
    );
  }

  const response = await apiClient.get<GetAssessmentByIdResponse>(
    `api/assessments/${params.assessmentId}`,
    {
      params: queryParams,
      signal,
    },
  );

  return response.data;
}

/**
 * Get framework details
 * Input: frameworkId (string)
 * Output: Framework with domains and fields
 */
async function getFrameworkDetails(
  params: GetFrameworkDetailsParams,
  signal?: AbortSignal,
): Promise<GetFrameworkDetailsResponse> {
  const queryParams = new URLSearchParams();

  if (params.version) {
    queryParams.append('version', params.version);
  }

  const response = await apiClient.get<GetFrameworkDetailsResponse>(
    `api/frameworks/${params.frameworkKey}`,
    {
      params: queryParams,
      signal,
    },
  );

  return response.data;
}


async function submitAssessment(params: SubmitAssessmentParam): Promise<SubmitAssessmentResponse> {
  const response = await apiClient.post<SubmitAssessmentResponse>(
    `api/assessments/${params.assessmentId}/submit`,
  );

  return response.data;
}

async function retryAssessment(params: SubmitAssessmentParam): Promise<SubmitAssessmentResponse> {
  const response = await apiClient.post<SubmitAssessmentResponse>(
    `api/assessments/${params.assessmentId}/retry`,
  );

  return response.data;
}

/**
 * Duplicate an assessment
 * Input: assessmentId (string), optional title (string) for the copy
 * Output: the new draft assessment id and the domain to resume from
 */
async function duplicateAssessment(params: DuplicateAssessmentParam): Promise<DuplicateAssessmentResponse> {
  const response = await apiClient.post<DuplicateAssessmentResponse>(
    `api/assessments/${params.assessmentId}/duplicate`,
    // Presence, not truthiness: an explicit empty title is a caller error and
    // must reach the server's validation rather than silently becoming a
    // body-less request that gets the generated name.
    params.title !== undefined ? { title: params.title } : undefined,
  );

  return response.data;
}

async function createVersion(params: CreateVersionParam): Promise<CreateVersionResponse> {
  const response = await apiClient.post<CreateVersionResponse>(
    `api/assessments/${params.assessmentId}/create-version`,
  );

  return response.data;
}



async function getAssessmentResults(
  params: GetAssessmentByIdParam,
  signal?: AbortSignal,
): Promise<GetAssessmentResultsResponse> {
  const response = await apiClient.get<GetAssessmentResultsResponse>(
    `api/assessments/${params.assessmentId}/results`,
    { signal },
  );

  const parsed = GetAssessmentResultsResponseSchema.safeParse(response.data);
  return parsed.success ? parsed.data : response.data;
}

/**
 * Get presigned download URL for assessment report
 */
async function getReportDownloadUrl(
  params: GetReportDownloadUrlParam,
): Promise<GetReportDownloadUrlResponse> {
  const response = await apiClient.get<GetReportDownloadUrlResponse>(
    `api/assessments/${params.assessmentId}/report/download-url`,
  );

  return response.data;
}

/**
 * Check assessment status (lightweight endpoint for polling)
 */
async function getAssessmentStatus(
  params: GetAssessmentStatusParam,
): Promise<GetAssessmentStatusResponse> {
  const response = await apiClient.get<GetAssessmentStatusResponse>(
    `api/assessments/${params.assessmentId}/status`,
  );

  return response.data;
}



// ============================================================================
// REACT QUERY HOOKS - Assessment CRUD Operations
// ============================================================================

export const useGetAllAssessments = (params?: Partial<GetAllAssessmentsQueries>) => {
  return useQuery<GetAllAssessmentsResponse>({
    queryKey: queryKeys.assessments.all(params),
    queryFn: ({ signal }) => getAllAssessments(params, signal),
    staleTime: 0, // Data is immediately stale
    refetchOnMount: 'always', // Always refetch on mount
    // Page/filter changes are a new cache entry, and with `staleTime: 0` every
    // one of them would otherwise drop to `undefined` and collapse the table
    // into its skeleton before the next page lands. Holding the previous page
    // keeps the rows on screen while the new ones load — `isFetching` is what
    // tables use to dim the pager meanwhile.
    placeholderData: keepPreviousData,
  });
};

export const useDeleteAssessment = () => {
  const queryClient = useQueryClient();
  const { t } = useTrans();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation<DeleteAssessmentResponse, Error, DeleteAssessmentParam>({
    mutationFn: deleteAssessment,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['assessments'] });
    },
    onError: (error) => {
      const errorMessage = getLocalizedErrorMessage(error, t, 'Failed to delete assessment');
      enqueueSnackbar(errorMessage, { variant: 'error' });
    },
  });
};

export const useCreateAssessment = () => {
  const queryClient = useQueryClient();
  const { t } = useTrans();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation<CreateAssessmentResponse, Error, CreateAssessmentRequest>({
    mutationFn: createAssessment,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['assessments'] });
    },
    onError: (error) => {
      const errorMessage = getLocalizedErrorMessage(error, t, 'Failed to create assessment');
      enqueueSnackbar(errorMessage, { variant: 'error' });
    },
  });
};

export const useUpdateAssessmentTitle = () => {
  const queryClient = useQueryClient();
  const { t } = useTrans();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation<
    UpdateAssessmentTitleResponse,
    Error,
    { assessmentId: string; title: string }
  >({
    mutationFn: ({ assessmentId, title }) => updateAssessmentTitle(assessmentId, title),
    onSuccess: (_data, variables) => {
      // Invalidate the specific assessment query
      queryClient.invalidateQueries({
        queryKey: ['assessments', variables.assessmentId],
      });
      // Invalidate all assessments list
      queryClient.invalidateQueries({ queryKey: ['assessments'] });
    },
    onError: (error) => {
      const errorMessage = getLocalizedErrorMessage(error, t, 'Failed to update assessment title');
      enqueueSnackbar(errorMessage, { variant: 'error' });
    },
  });
};

export const useGetAssessmentById = (
  params: GetAssessmentByIdParam,
  query?: GetAssessmentByIdQuery,
  queryOptions?: Omit<UseQueryOptions<GetAssessmentByIdResponse>, 'queryKey' | 'queryFn'>,
) => {
  return useQuery<GetAssessmentByIdResponse>({
    queryKey: ['assessments', params, query],
    queryFn: ({ signal }) => getAssessmentById(params, query, signal),
    enabled: !!params.assessmentId,
    ...queryOptions,
  });
};

export const useGetFrameworkDetails = (params: GetFrameworkDetailsParams) => {
  return useQuery<GetFrameworkDetailsResponse>({
    queryKey: [...queryKeys.framework.byKey(params.frameworkKey, params.version), params],
    queryFn: ({ signal }) => getFrameworkDetails(params, signal),
    enabled: !!params.frameworkKey,
  });
};



export const useRetryAssessment = () => {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTrans();

  return useMutation<SubmitAssessmentResponse, Error, SubmitAssessmentParam>({
    mutationFn: retryAssessment,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['assessments'] });
      enqueueSnackbar(t('app.assessments.retry.success'), {
        variant: 'success',
        anchorOrigin: { vertical: 'top', horizontal: 'right' },
      });
    },
    onError: (error) => {
      const errorMessage = getLocalizedErrorMessage(error, t, t('app.assessments.retry.error'));
      enqueueSnackbar(errorMessage, {
        variant: 'error',
        anchorOrigin: { vertical: 'top', horizontal: 'right' },
      });
    },
  });
};

export const useDuplicateAssessment = () => {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTrans();

  return useMutation<DuplicateAssessmentResponse, Error, DuplicateAssessmentParam>({
    mutationFn: duplicateAssessment,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['assessments'] });
      enqueueSnackbar(t('app.assessments.duplicate.success'), {
        variant: 'success',
        anchorOrigin: { vertical: 'top', horizontal: 'right' },
      });
    },
    onError: (error) => {
      const errorMessage = getLocalizedErrorMessage(error, t, t('app.assessments.duplicate.error'));
      enqueueSnackbar(errorMessage, {
        variant: 'error',
        anchorOrigin: { vertical: 'top', horizontal: 'right' },
      });
    },
  });
};

export const useCreateVersion = () => {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTrans();

  return useMutation<CreateVersionResponse, Error, CreateVersionParam>({
    mutationFn: createVersion,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['assessments'] });
      enqueueSnackbar(t('app.assessments.newVersion.success'), {
        variant: 'success',
        anchorOrigin: { vertical: 'top', horizontal: 'right' },
      });
    },
    onError: (error) => {
      const errorMessage = getLocalizedErrorMessage(error, t, t('app.assessments.newVersion.error'));
      enqueueSnackbar(errorMessage, {
        variant: 'error',
        anchorOrigin: { vertical: 'top', horizontal: 'right' },
      });
    },
  });
};

export const useSubmitAssessment = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTrans();
  // Already cached by the time anyone reaches a summary screen, so this is a
  // read rather than a request. Falls back to the list when the flag is off or
  // has not resolved — the run screen's route is gated by the same flag and
  // would bounce to the dashboard.
  const { data: features } = useFeatureFlags();
  const progressEnabled = features?.[FeatureFlagKey.ASSESSMENT_PROGRESS] ?? false;

  return useMutation<SubmitAssessmentResponse, Error, SubmitAssessmentParam>({
    mutationFn: submitAssessment,
    onSuccess: (_data, variables) => {
      // Refresh the assessment caches so any re-entry sees the submitted state.
      queryClient.invalidateQueries({
        queryKey: ['assessments', { assessmentId: variables.assessmentId }],
      });
      queryClient.invalidateQueries({ queryKey: ['assessments'] });

      // Show success message
      enqueueSnackbar(
        'Your assessment has been successfully submitted and is now being evaluated.',
        {
          variant: 'success',
          anchorOrigin: { vertical: 'top', horizontal: 'right' },
        },
      );

      // Straight to the run screen rather than back to the list.
      //
      // Submitting starts 30-45 minutes of work the user cannot otherwise see,
      // and the list row was the only feedback that it had begun. `replace` so
      // Back returns to wherever they came from rather than re-entering the
      // summary of an assessment that can no longer be edited.
      navigate(
        progressEnabled
          ? routes.assessmentProgress(variables.assessmentId)
          : routes.assessmentList(),
        { replace: true },
      );
    },
    onError: (error) => {
      const errorMessage = getLocalizedErrorMessage(error, t, 'Failed to submit assessment');
      enqueueSnackbar(errorMessage, {
        variant: 'error',
        anchorOrigin: { vertical: 'top', horizontal: 'right' },
      });
    },
  });
};


export const useGetAssessmentResults = (
  params: GetAssessmentByIdParam,
  queryOptions?: Omit<UseQueryOptions<GetAssessmentResultsResponse>, 'queryKey' | 'queryFn'>,
) => {
  return useQuery<GetAssessmentResultsResponse>({
    queryKey: [...queryKeys.assessments.results(params.assessmentId), params],
    queryFn: ({ signal }) => getAssessmentResults(params, signal),
    ...queryOptions,
  });
};



/**
 * React Query mutation hook for downloading assessment report
 *
 * Automatically handles:
 * - Opening presigned URL in new tab for download
 * - Success notification via snackbar
 * - Error handling and user feedback
 *
 * @example
 * ```tsx
 * const reportDownloadMutation = useGetReportDownloadUrl();
 *
 * const handleExport = () => {
 *   reportDownloadMutation.mutate({ assessmentId });
 * };
 *
 * <Button
 *   onClick={handleExport}
 *   disabled={reportDownloadMutation.isPending}
 * >
 *   Export
 * </Button>
 * ```
 */
export const useGetReportDownloadUrl = () => {
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTrans();
  return useMutation<GetReportDownloadUrlResponse, Error, GetReportDownloadUrlParam>({
    mutationFn: getReportDownloadUrl,
    onSuccess: (data) => {
      // Open the presigned URL in new tab to trigger download
      window.open(data.s3DownloadUrl, '_blank');

      enqueueSnackbar('Report download started', {
        variant: 'success',
        anchorOrigin: { vertical: 'top', horizontal: 'right' },
        autoHideDuration: 3000,
      });
    },
    onError: (error: unknown) => {
      const errorMessage = getLocalizedErrorMessage(error, t, 'Failed to get report download URL');

      enqueueSnackbar(errorMessage, {
        variant: 'error',
        anchorOrigin: { vertical: 'top', horizontal: 'right' },
        autoHideDuration: 6000,
      });
    },
  });
};



/**
 * Custom hook for polling assessment status with optimistic progress updates
 *
 * Implements the pattern:
 * 1. Poll status endpoint every 5 seconds if PROCESSING or SUBMITTED
 * 2. Cancel any outgoing refetches to prevent race conditions
 * 3. Fetch actual status from API
 * 4. Reconcile cache with real values from server
 *
 * @param assessmentId - The assessment to poll
 * @param currentStatus - Current assessment status
 * @param params - Query params for cache key matching
 */
export const useAssessmentStatusPolling = (
  assessmentId: string,
  currentStatus: string,
  params?: Partial<GetAllAssessmentsQueries>,
) => {
  const queryClient = useQueryClient();

  useEffect(() => {
    // Only poll if status is PROCESSING
    if (currentStatus !== AssessmentStatus.PROCESSING && currentStatus !== AssessmentStatus.SUBMITTED) return;

    const pollStatus = async () => {
      // Declare snapshot outside try-catch so it's accessible in catch block
      let previousData: GetAllAssessmentsResponse | undefined;

      try {

        // Step 2: Snapshot the previous value
        previousData = queryClient.getQueryData<GetAllAssessmentsResponse>(
          queryKeys.assessments.all(params),
        );

        // Step 4: Fetch actual status from API
        const newStatus = await getAssessmentStatus({ assessmentId });

        // Step 5: Reconcile cache with actual values from server. This is an
        // optimistic in-place patch for instant feedback (badge/progress bar
        // flip immediately). Note the status endpoint does NOT return
        // completedAt, so the duration column cannot be filled from here.
        if (previousData) {
          queryClient.setQueryData<GetAllAssessmentsResponse>(
            queryKeys.assessments.all(params),
            (old) => {
              if (!old) return old
              return {
                ...old,
                rows: old.rows.map((assessment) =>
                  assessment.id === assessmentId
                    ? {
                      ...assessment,
                      status: newStatus.status as AssessmentStatus,
                      progress: newStatus.progress,
                      score: newStatus.score,
                      updatedAt: newStatus.updatedAt,
                    }
                    : assessment,
                ),
              }
            }
          );
        }

        // Step 6: On a terminal transition (COMPLETED / FAILED), refetch the
        // list from the backend so authoritative fields the status payload
        // omits — completedAt (and therefore the duration column), the final
        // score, failureReason — are correct without a manual page refresh.
        if (
          newStatus.status === AssessmentStatus.COMPLETED ||
          newStatus.status === AssessmentStatus.FAILED
        ) {
          await queryClient.invalidateQueries({ queryKey: queryKeys.assessments.allPrefix });
        }
      } catch (error) {
        console.error('[AssessmentPolling] Failed to poll assessment status:', error);
      }
    };

    // Initial poll
    pollStatus();

    // Set up interval for polling every 5 seconds
    const intervalId = setInterval(pollStatus, 5000);

    // Cleanup on unmount or when status changes
    return () => {
      clearInterval(intervalId);
    };
  }, [assessmentId, currentStatus, params, queryClient]);
};
